import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Loader2, ChevronLeft, User, FileText } from "lucide-react";

export default function BlogReader() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`https://340realestate.com/api/blogs/${slug}`)
      .then(r => {
         if (!r.ok) throw new Error(r.status);
         return r.json();
      })
      .then(d => {
         if (!d.blog) setNotFound(true);
         else setBlog(d.blog);
      })
      .catch(e => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <main className="min-h-screen bg-brand-light flex flex-col font-sans">
        <Navbar isTransparent={false} />
        <div className="flex flex-col items-center justify-center py-40 gap-4 opacity-60 flex-grow">
          <Loader2 className="w-10 h-10 text-tropical-500 animate-spin" />
          <p className="text-[13px] font-bold uppercase tracking-widest text-ink-600">Loading Article…</p>
        </div>
        <Footer />
      </main>
    );
  }

  if (notFound || !blog) {
    return (
      <main className="min-h-screen bg-brand-light flex flex-col font-sans">
        <Navbar isTransparent={false} />
        <div className="flex flex-col items-center justify-center py-40 text-center px-6 flex-grow">
          <FileText className="w-16 h-16 text-ink-900/20 mb-6" />
          <h1 className="text-[2.5rem] font-bold text-ink-900 mb-4 font-display">Article Not Found</h1>
          <p className="text-[15px] text-ink-600 mb-8">The blog post you're looking for doesn't exist or has been removed.</p>
          <Link to="/blogs" className="flex items-center gap-2 bg-ink-900 text-white px-8 py-4 rounded-xl text-[13px] font-bold uppercase tracking-widest hover:bg-ink-800 transition-colors shadow-md">
            <ChevronLeft className="w-4 h-4" /> Back to Blogs
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const blocks = Array.isArray(blog.content_blocks) ? blog.content_blocks : [];
  const coverImageUrl = blog.cover_image 
    ? (blog.cover_image.startsWith('http') ? blog.cover_image : `https://340realestate.com${blog.cover_image}`)
    : null;

  return (
    <main className="min-h-screen bg-brand-light flex flex-col font-sans">
      <Navbar isTransparent={false} />

      <div className="relative bg-ink-900">
        {coverImageUrl && (
          <div className="absolute inset-0 opacity-20">
            <img src={coverImageUrl} alt="Background cover" className="w-full h-full object-cover blur-sm" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/80 to-transparent" />
          </div>
        )}
        <div className="relative max-w-[900px] mx-auto px-6 py-20 md:py-32 flex flex-col items-center text-center">
          <Link to="/blogs" className="inline-flex items-center gap-2 text-[11px] font-bold text-tropical-500 uppercase tracking-[0.2em] hover:text-white transition-colors mb-10 group">
            <ChevronLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
            Back to Journal
          </Link>

          <h1 className="text-[2.5rem] md:text-[4rem] font-bold text-white mb-8 leading-tight drop-shadow-lg font-display">
            {blog.title}
          </h1>

          <div className="flex flex-wrap justify-center items-center gap-6 text-[12px] font-semibold text-white/70 uppercase tracking-wider">
            <div className="flex items-center gap-2"><User className="w-4 h-4 text-tropical-500" /> BY {blog.author}</div>
          </div>
        </div>
      </div>

      <div className="max-w-[850px] mx-auto w-full px-6 -mt-10 relative z-10 mb-20 lg:mb-32 flex-grow">
         
         <div className="bg-white rounded-[2rem] shadow-xl p-8 sm:p-12 md:p-16 border border-[#E8E3DC]">
           
           <p className="text-[1.2rem] sm:text-[1.5rem] text-ink-900 leading-relaxed mb-12 font-display">
             {blog.excerpt}
           </p>

           <article className="flex flex-col gap-8">
             {blocks.map((block) => {
               if (block.type === "heading") {
                 if (block.level === "h2") return <h2 key={block.id} className="text-[2rem] sm:text-[2.5rem] font-bold text-ink-900 leading-tight mt-6 font-display">{block.value}</h2>;
                 if (block.level === "h3") return <h3 key={block.id} className="text-[1.5rem] sm:text-[1.8rem] font-bold text-ink-900 leading-tight mt-4 font-display">{block.value}</h3>;
                 return <h4 key={block.id} className="text-[1.3rem] font-bold text-ink-900 uppercase tracking-wider mt-4">{block.value}</h4>;
               }

               if (block.type === "paragraph") {
                 return (
                   <p key={block.id} className={`text-[16px] sm:text-[18px] text-ink-600 leading-[1.8] tracking-wide ${block.dropCap ? "first-letter:text-[4rem] first-letter:font-bold first-letter:text-tropical-500 first-letter:mr-3 first-letter:float-left first-letter:leading-[0.8]" : ""}`}>
                     {block.value}
                   </p>
                 );
               }

               if (block.type === "calligraphy") {
                 return (
                   <div key={block.id} className="my-8 flex justify-center py-6 border-y border-brand-light">
                     <p className="text-[2rem] sm:text-[2.8rem] text-tropical-500 leading-relaxed italic text-center max-w-2xl px-6 font-display">
                       {block.value}
                     </p>
                   </div>
                 );
               }

               if (block.type === "quote") {
                 return (
                   <blockquote key={block.id} className="relative my-8 px-8 sm:px-12 py-6 bg-brand-light rounded-2xl border-l-4 border-tropical-500">
                     <div className="text-[1.2rem] sm:text-[1.4rem] font-bold text-ink-900 mb-4 relative z-10 font-display">
                       "{block.value}"
                     </div>
                     {block.author && <div className="text-[12px] font-bold text-ink-600 uppercase tracking-wider">— {block.author}</div>}
                   </blockquote>
                 );
               }

               if (block.type === "image") {
                 const imageUrl = block.value.startsWith('http') ? block.value : `https://340realestate.com${block.value}`;
                 return (
                   <div key={block.id} className={`my-8 rounded-2xl overflow-hidden shadow-sm border border-[#E8E3DC] ${block.size === "inline" ? "max-w-xl mx-auto" : "w-full"}`}>
                     <img src={imageUrl} alt={block.caption || "Blog visual"} className="w-full h-auto object-cover max-h-[600px] bg-gray-100" />
                     {block.caption && <p className="p-4 bg-white text-[12px] text-center font-bold text-ink-600/50 uppercase tracking-wider">{block.caption}</p>}
                   </div>
                 );
               }

               if (block.type === "link") {
                 return (
                   <div key={block.id} className="my-8 flex justify-center">
                     <a href={block.url || "#"} className="inline-flex items-center justify-center gap-2 bg-ink-900 text-white px-10 py-5 rounded-xl text-[13px] font-bold uppercase tracking-widest hover:bg-ink-800 hover:scale-105 transition-all shadow-md">
                       {block.value || "Click Here"}
                     </a>
                   </div>
                 );
               }

               return null;
             })}
           </article>
         </div>
      </div>

      <Footer />
    </main>
  );
}
